// useChatUserID.ts
"use client"

import { uid } from '@/utils/functions'; // Utility function to generate a unique ID
import { useEffect, useState } from 'react';

/**
 * Custom hook to manage a unique user ID for chat sessions.
 * 
 * It uses the provided `sessionID` if available.
 * Otherwise, it tries to retrieve a stored ID from localStorage,
 * or creates a new one and stores it.
 *
 * @param sessionID Optional session ID to use as the user ID
 * @returns A string or number representing the chat user ID
 */
const useChatUserID = (sessionID?: number) => {
  // State to hold the current chat user ID
  const [chatUserID, setChatUserID] = useState<string | number>('');

  useEffect(() => {
    // Try to get any previously stored user ID from localStorage
    const storedUserID = localStorage.getItem('chatDrawerUserID');

    if (sessionID) {
      // If a session ID is provided, use it directly
      setChatUserID(sessionID);
    } else if (storedUserID) {
      // If there's a stored ID in localStorage, use it
      setChatUserID(storedUserID);
    } else {
      // Otherwise, generate a new unique ID and store it
      const newUserID = uid();
      localStorage.setItem('chatDrawerUserID', newUserID);
      setChatUserID(newUserID);
    }
  }, [sessionID]);

  return chatUserID;
};

export default useChatUserID;
